<template>
  <div class="edit-property">
    <v-row class="mb-4">
      <v-col cols="12" sm="12" md="12" class="d-flex">
        <div class="button-container">
          <button class="add-button" @click="openUnitPopup">Add Unit/Room Name</button>
        </div>
      </v-col>
    </v-row>

    <!-- Unit Popup Modal -->
    <v-dialog v-model="unitDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Add Unit</v-card-title>
        <v-card-text>
          <div class="form-group">
            <label for="unitName">Unit Name</label>
            <input
              type="text"
              id="unitName"
              v-model="newUnit.unitName"
              required
              placeholder="Unit Name"
            />
          </div>
          <div class="form-group">
            <label for="isOccupied">Occupied</label>
            <input
              type="checkbox"
              id="isOccupied"
              v-model="newUnit.isOccupied"
            />
            <span>{{ newUnit.isOccupied ? "Yes" : "No" }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="addUnit">Add Unit</v-btn>
          <v-btn color="secondary" @click="unitDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Content Wrapper -->
    <div class="content-wrapper">
      <!-- Property Form (Left Column) -->
      <div class="form-container">
        <h1>Edit Property</h1>
        <form @submit.prevent="submitProperty">
          <div class="form-group">
            <label for="displayName">Display Name</label>
            <input
              type="text"
              id="displayName"
              v-model="property.displayName"
              required
            />
          </div>

          <!-- Landlord Selector -->
          <div class="form-group">
            <label for="landlord">Select Landlord</label>
            <LandlordSelector
              :properties="landlordList"
              :selected-property="property.selectedLandlord"
              @property-selected="updateSelectedLandlord"
            />
          </div>

          <div class="form-group">
            <label for="address">Address</label>
            <div class="address-fields">
              <input
                type="text"
                placeholder="Building No. & Name"
                v-model="property.address.building"
                required
              />
              <input
                type="text"
                placeholder="Street Name"
                v-model="property.address.street"
                required
              />
              <input
                type="text"
                placeholder="Suburb"
                v-model="property.address.suburb"
                required
              />
              <input
                type="text"
                placeholder="City"
                v-model="property.address.city"
                required
              />
              <input
                type="text"
                placeholder="Postal Code"
                v-model="property.address.postalCode"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="category">Category</label>
            <select id="category" v-model="property.category" required>
              <option disabled value="">Select Category</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>

          <div class="form-group">
            <label for="type">Type</label>
            <select id="type" v-model="property.type" required>
              <option disabled value="">Select Type</option>
              <option value="house">House</option>
              <option value="apartment">Apartment</option>
            </select>
          </div>

          <!-- Radio Buttons for Is Vacant -->
          <div class="form-group">
            <label for="vacant">Is Vacant</label>
            <div class="radio-group">
              <label class="radio-label">
                <input
                  type="radio"
                  id="vacant-yes"
                  :value="true"
                  v-model="property.isVacant"
                  required
                />
                <span class="radio-custom"></span>
                Yes
              </label>
              <label class="radio-label">
                <input
                  type="radio"
                  id="vacant-no"
                  :value="false"
                  v-model="property.isVacant"
                  required
                />
                <span class="radio-custom"></span>
                No
              </label>
            </div>
            <p>Current isVacant value: {{ property.isVacant }}</p>
          </div>

          <div class="form-group">
            <label for="location">Location</label>
            <p>Latitude: {{ property.location.lat }}</p>
            <p>Longitude: {{ property.location.lng }}</p>
          </div>

          <button type="submit">Update Property</button>
        </form>
      </div>

      <!-- Units Table (Right Column) -->
      <div class="units-container">
        <h2>Units</h2>
        <table>
          <thead>
            <tr>
              <th>Unit Name</th>
              <th>Occupied</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(unit, index) in units" :key="unit.id">
              <td>{{ unit.unitName }}</td>
              <td>{{ unit.isOccupied ? "Yes" : "No" }}</td>
              <td>
                <button @click="deleteUnit(index)">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import LandlordSelector from "./LandlordSelector.vue";
import {
  getDoc,
  doc,
  updateDoc,
  getDocs,
  collection,
  getFirestore,
  addDoc,
  deleteDoc,
} from "firebase/firestore";

const db = getFirestore();

export default {
  name: "EditProperty",
  components: {
    LandlordSelector,
  },
  data() {
    return {
      property: {
        displayName: "",
        address: {
          building: "",
          street: "",
          suburb: "",
          city: "",
          postalCode: "",
        },
        category: "",
        type: "",
        isVacant: false, // Default to "No"
        location: { lat: 0, lng: 0 },
        selectedLandlord: null,
      },
      landlordList: [],
      units: [], // Units array to store added units
      unitDialog: false, // Control the dialog visibility
      newUnit: {
        unitName: "",
        isOccupied: false,
      },
    };
  },
  async created() {
    const propertyId = this.$route.params.id;

    const propertyDoc = await getDoc(doc(db, "properties", propertyId));
    if (propertyDoc.exists()) {
      this.property = { ...propertyDoc.data(), id: propertyDoc.id };
      // Fetch and set landlords after the property is loaded
      this.fetchLandlords(propertyDoc.data().selectedLandlord);
      // Fetch units for this property
      this.fetchUnits(propertyDoc.id);
    }
  },
  methods: {
    async fetchLandlords(selectedLandlordRef) {
  try {
    // Fetch all landlords
    const querySnapshot = await getDocs(collection(db, "landlords"));
    this.landlordList = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Find the selected landlord object
    const selectedLandlord = this.landlordList.find(
      (landlord) => landlord.reference === selectedLandlordRef
    );

    // Assign the selected landlord object to the property
    if (selectedLandlord) {
      this.property.selectedLandlord = selectedLandlord;
    } else {
      this.property.selectedLandlord = null; // Default to null if no match is found
    }
  } catch (error) {
    console.error("Error fetching landlords:", error);
  }
},
    async fetchUnits(propertyId) {
      try {
        const querySnapshot = await getDocs(collection(db, "properties", propertyId, "units"));
        this.units = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    },
    openUnitPopup() {
      this.unitDialog = true;
    },
    async addUnit() {
      const newUnit = {
        unitName: this.newUnit.unitName,
        isOccupied: this.newUnit.isOccupied,
      };

      try {
        const unitRef = await addDoc(collection(db, "properties", this.property.id, "units"), newUnit);
        this.units.push({ ...newUnit, id: unitRef.id });
        this.newUnit.unitName = "";
        this.newUnit.isOccupied = false;
        this.unitDialog = false;
      } catch (error) {
        console.error("Error adding unit:", error);
      }
    },
    async deleteUnit(index) {
      const unitId = this.units[index].id;
      try {
        await deleteDoc(doc(db, "properties", this.property.id, "units", unitId));
        this.units.splice(index, 1);
      } catch (error) {
        console.error("Error deleting unit:", error);
      }
    },
    async submitProperty() {
      try {
        console.log("isVacant before saving:", this.property.isVacant, typeof this.property.isVacant); // Debugging

        const propertyData = {
          ...this.property,
          isVacant: this.property.isVacant, // No need for Boolean() conversion
          selectedLandlord: this.property.selectedLandlord?.reference || null,
        };

        console.log("propertyData before saving:", propertyData); // Debugging

        // Save the updated property data
        await updateDoc(doc(db, "properties", this.property.id), propertyData);
        alert("Property updated successfully!");

        // Fetch the updated document from the database
        const updatedDoc = await getDoc(doc(db, "properties", this.property.id));
        const updatedData = updatedDoc.data();

        console.log("isVacant after saving:", updatedData.isVacant, typeof updatedData.isVacant); // Debugging

        this.$router.push("/properties");
      } catch (error) {
        console.error("Error updating property:", error);
      }
    },
    updateSelectedLandlord(landlord) {
      this.property.selectedLandlord = landlord;
    },
  },
};
</script>


<style scoped>
.edit-property {
  padding: 20px;
  background: #f9f9f9;
  font-family: Arial, sans-serif;
}

.button-container {
  margin-left: auto;
  padding: 10px;
}

.add-button {
  background-color: #f1c284;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-button:hover {
  background-color: #e0a858;
}

.add-button:focus {
  outline: none;
}

.content-wrapper {
  display: flex;
  gap: 20px;
}

.form-container {
  flex: 1;
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.units-container {
  flex: 1;
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

h1,
h2 {
  text-align: center;
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
  color: #555;
}

input[type="text"],
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

button[type="submit"] {
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 16px;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

th {
  background-color: #f5f5f5;
}

button {
  padding: 8px 12px;
  background-color: #ff4d4d;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: #cc0000;
}

.radio-group {
  display: flex;
  gap: 10px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
}

input[type="radio"] {
  margin-right: 8px;
}
</style>